import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, BadgeCheck, AlertCircle, Clock, Play, Paperclip, X, Pencil, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const API_URL = `${window.location.origin}/api`;
const today = () => new Date().toISOString().slice(0, 10);

function ReviewBadge({ review, reviewerName }) {
  if (review === "parfait")
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-[0.7rem] font-bold text-emerald-700" data-testid="review-badge-parfait">
        <BadgeCheck size={12} /> Parfait{reviewerName ? ` · ${reviewerName}` : ""}
      </span>
    );
  if (review === "corriger")
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-1 text-[0.7rem] font-bold text-red-600" data-testid="review-badge-corriger">
        <AlertCircle size={12} /> À corriger{reviewerName ? ` · ${reviewerName}` : ""}
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[0.7rem] font-bold text-slate-500" data-testid="review-badge-none">
      <Clock size={12} /> En attente d'évaluation
    </span>
  );
}

export function WorkCard({ w, children }) {
  return (
    <article className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5" data-testid={`work-card-${w.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[0.7rem] font-bold uppercase tracking-[0.14em] text-korban-blue">{w.company}</p>
          <h3 className="mt-1 font-display text-lg font-semibold leading-snug text-korban-navy">{w.title}</h3>
        </div>
        <ReviewBadge review={w.review} reviewerName={w.reviewer_name} />
      </div>
      {w.description && <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{w.description}</p>}
      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-500">
        <span className="rounded bg-slate-100 px-2 py-0.5 font-semibold">{w.work_date}</span>
        <span className={`rounded px-2 py-0.5 font-semibold ${w.status === "Terminé" ? "bg-emerald-50 text-emerald-700" : w.status === "En cours" ? "bg-amber-50 text-amber-700" : "bg-sky-50 text-sky-700"}`}>{w.status}</span>
        {w.dept && <span className="rounded bg-korban-navy2/10 px-2 py-0.5 font-semibold text-korban-navy2">{w.dept}</span>}
        {w.author_name && <span className="font-semibold text-slate-600">— {w.author_name}</span>}
      </div>
      {w.executed_at && (
        <p className="mt-2 text-xs text-slate-500" data-testid={`work-executed-${w.id}`}>
          Exécuté le {w.executed_at.slice(0, 16).replace("T", " ")}
        </p>
      )}
      {w.execution_note && (
        <p className="mt-2 rounded-lg bg-slate-50 p-3 text-sm leading-relaxed text-slate-600">
          <b className="text-korban-navy">Exécution :</b> {w.execution_note}
        </p>
      )}
      {w.attachment_path && (
        <a
          href={`${API_URL}/files/${w.attachment_path}`}
          target="_blank"
          rel="noopener"
          data-testid={`work-attachment-${w.id}`}
          className="mt-2.5 inline-flex items-center gap-1.5 rounded-lg bg-korban-blue/10 px-3 py-1.5 text-xs font-bold text-korban-navy2 transition-colors hover:bg-korban-blue/20"
        >
          <Paperclip size={13} /> {w.attachment_name || "Pièce jointe"}
        </a>
      )}
      {children}
    </article>
  );
}

export default function MyWorks({ user }) {
  const [works, setWorks] = useState(null);
  const [busy, setBusy] = useState(false);
  const [execId, setExecId] = useState(null);
  const [execNote, setExecNote] = useState("");
  const [execBusy, setExecBusy] = useState(false);
  const fileRef = useRef(null);
  const [editId, setEditId] = useState(null);
  const [editBusy, setEditBusy] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [delFor, setDelFor] = useState(null);
  const [delReason, setDelReason] = useState("");
  const [delBusy, setDelBusy] = useState(false);

  const startEdit = (w) => {
    setEditId(w.id);
    setEditForm({ company: w.company, title: w.title, description: w.description, work_date: w.work_date, status: w.status });
  };

  const saveEdit = async (w) => {
    setEditBusy(true);
    try {
      await api.patch(`/works/${w.id}`, editForm);
      toast.success("Travail modifié.");
      setEditId(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setEditBusy(false);
    }
  };

  const deleteWork = async (w) => {
    setDelBusy(true);
    try {
      await api.delete(`/works/${w.id}`, { data: { reason: delReason } });
      toast.success("Travail supprimé. Le gérant peut le restaurer depuis la corbeille.");
      setDelFor(null);
      setDelReason("");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDelBusy(false);
    }
  };
  const [form, setForm] = useState({ company: "", title: "", description: "", work_date: today(), status: "À effectuer" });

  const executeWork = async (w) => {
    setExecBusy(true);
    try {
      const fd = new FormData();
      fd.append("note", execNote);
      const f = fileRef.current?.files?.[0];
      if (f) fd.append("file", f);
      await api.post(`/works/${w.id}/execute`, fd);
      toast.success("Travail exécuté. Félicitations !");
      setExecId(null);
      setExecNote("");
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setExecBusy(false);
    }
  };

  const load = () => api.get("/works", { params: { mine: "1" } }).then((r) => setWorks(r.data));
  useEffect(() => {
    load().catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/works", form);
      toast.success("Travail enregistré.");
      setForm({ company: "", title: "", description: "", work_date: today(), status: "À effectuer" });
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const byCompany = {};
  (works || []).forEach((w) => {
    (byCompany[w.company] = byCompany[w.company] || []).push(w);
  });

  return (
    <div className="grid gap-8">
      <form onSubmit={submit} data-testid="new-work-form" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <h2 className="font-display text-xl font-bold text-korban-navy">Nouveau travail à effectuer</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="w-company" className="mb-1 block text-xs font-bold text-slate-600">Entreprise cliente</label>
            <input id="w-company" required minLength={2} value={form.company} onChange={set("company")}
              placeholder="Nom de l'entreprise" data-testid="work-input-company" className="field field-light" />
          </div>
          <div>
            <label htmlFor="w-title" className="mb-1 block text-xs font-bold text-slate-600">Intitulé du travail</label>
            <input id="w-title" required minLength={2} value={form.title} onChange={set("title")}
              placeholder="Ex. Établissement des bulletins de paie" data-testid="work-input-title" className="field field-light" />
          </div>
          <div>
            <label htmlFor="w-date" className="mb-1 block text-xs font-bold text-slate-600">Date</label>
            <input id="w-date" type="date" required value={form.work_date} onChange={set("work_date")}
              data-testid="work-input-date" className="field field-light" />
          </div>
          <div>
            <label htmlFor="w-status" className="mb-1 block text-xs font-bold text-slate-600">Statut</label>
            <select id="w-status" value={form.status} onChange={set("status")} data-testid="work-select-status" className="field field-light">
              <option>À effectuer</option>
              <option>En cours</option>
              <option>Terminé</option>
            </select>
          </div>
        </div>
        <div className="mt-4">
          <label htmlFor="w-desc" className="mb-1 block text-xs font-bold text-slate-600">Description</label>
          <textarea id="w-desc" rows={3} value={form.description} onChange={set("description")}
            placeholder="Détails du travail réalisé…" data-testid="work-input-description" className="field field-light resize-none" />
        </div>
        <button
          type="submit" disabled={busy} data-testid="work-submit-button"
          className="mt-4 inline-flex items-center gap-2 rounded-lg bg-korban-navy px-6 py-3 text-sm font-bold text-korban-gold transition-all hover:-translate-y-0.5 hover:bg-korban-navy2 disabled:opacity-60"
        >
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
          Enregistrer
        </button>
      </form>

      <div>
        <h2 className="font-display text-xl font-bold text-korban-navy">Mon historique par entreprise</h2>
        {!works ? (
          <p className="mt-4 flex items-center gap-2 text-slate-500"><Loader2 size={16} className="animate-spin" /> Chargement…</p>
        ) : Object.keys(byCompany).length === 0 ? (
          <p className="mt-4 rounded-xl border border-dashed border-slate-300 p-6 text-center text-slate-500" data-testid="my-history-empty">
            Aucun travail enregistré pour l'instant.
          </p>
        ) : (
          <div className="mt-4 space-y-8" data-testid="my-history-list">
            {Object.entries(byCompany).map(([company, entries]) => (
              <div key={company}>
                <h3 className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-korban-blue">
                  <span className="h-2 w-2 rotate-45 bg-korban-gold" /> {company}
                  <span className="font-sans text-xs font-semibold normal-case text-slate-400">({entries.length})</span>
                </h3>
                <div className="space-y-3">
                  {entries.map((w) => (
                    <WorkCard key={w.id} w={{ ...w, company: "" }}>
                      {w.review === null && (
                        <div className="mt-4 flex flex-wrap gap-2">
                          {w.status !== "Terminé" && (
                            <button
                              onClick={() => { setExecId(w.id); setExecNote(""); }}
                              data-testid={`execute-button-${w.id}`}
                              className="inline-flex items-center gap-1.5 rounded-lg bg-korban-navy px-4 py-2 text-xs font-bold text-korban-gold transition-all hover:-translate-y-0.5 hover:bg-korban-navy2"
                            >
                              <Play size={13} /> Exécuter ce travail
                            </button>
                          )}
                          <button
                            onClick={() => startEdit(w)}
                            data-testid={`edit-button-${w.id}`}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-white px-4 py-2 text-xs font-bold text-korban-navy2 ring-1 ring-slate-200 transition-all hover:-translate-y-0.5 hover:bg-slate-50"
                          >
                            <Pencil size={13} /> Modifier
                          </button>
                          <button
                            onClick={() => { setDelFor(w.id); setDelReason(""); }}
                            data-testid={`delete-button-${w.id}`}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-white px-4 py-2 text-xs font-bold text-red-600 ring-1 ring-red-200 transition-all hover:-translate-y-0.5 hover:bg-red-50"
                          >
                            <Trash2 size={13} /> Supprimer
                          </button>
                        </div>
                      )}
                      {delFor === w.id && (
                        <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-4" data-testid={`delete-panel-${w.id}`}>
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-bold text-red-600">Motif de la suppression</p>
                            <button onClick={() => setDelFor(null)} data-testid={`delete-cancel-${w.id}`} className="text-slate-400 hover:text-slate-600" aria-label="Annuler">
                              <X size={16} />
                            </button>
                          </div>
                          <textarea
                            rows={2}
                            value={delReason}
                            onChange={(e) => setDelReason(e.target.value)}
                            placeholder="Expliquez pourquoi vous supprimez ce travail (obligatoire)…"
                            data-testid={`delete-reason-${w.id}`}
                            className="field field-light mt-3 resize-none text-sm"
                          />
                          <button
                            onClick={() => deleteWork(w)}
                            disabled={delBusy || !delReason.trim()}
                            data-testid={`delete-confirm-${w.id}`}
                            className="mt-3 inline-flex items-center gap-2 rounded-lg bg-red-600 px-5 py-2.5 text-xs font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {delBusy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                            Confirmer la suppression
                          </button>
                        </div>
                      )}
                      {execId === w.id && (
                          <div className="mt-4 rounded-xl border border-korban-blue/30 bg-korban-blue/5 p-4" data-testid={`execute-panel-${w.id}`}>
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-bold text-korban-navy">Exécuter ce travail</p>
                              <button onClick={() => setExecId(null)} data-testid={`execute-cancel-${w.id}`} className="text-slate-400 hover:text-slate-600" aria-label="Annuler">
                                <X size={16} />
                              </button>
                            </div>
                            <input
                              type="text"
                              value={execNote}
                              onChange={(e) => setExecNote(e.target.value)}
                              placeholder="Commentaire d'exécution (optionnel)"
                              data-testid={`execute-note-${w.id}`}
                              className="field field-light mt-3 text-sm"
                            />
                            <input
                              type="file"
                              ref={fileRef}
                              accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt,.ppt,.pptx"
                              data-testid={`execute-file-${w.id}`}
                              className="mt-3 block w-full text-xs text-slate-600 file:mr-3 file:cursor-pointer file:rounded-lg file:border-0 file:bg-korban-navy file:px-4 file:py-2 file:text-xs file:font-bold file:text-korban-gold"
                            />
                            <button
                              onClick={() => executeWork(w)}
                              disabled={execBusy}
                              data-testid={`execute-confirm-${w.id}`}
                              className="mt-3 inline-flex items-center gap-2 rounded-lg bg-korban-navy px-5 py-2.5 text-xs font-bold text-korban-gold transition-all hover:-translate-y-0.5 hover:bg-korban-navy2 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {execBusy ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
                              Valider l'exécution
                            </button>
                          </div>
                      )}
                      {delFor === w.id && (
                        <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-4" data-testid={`delete-panel-${w.id}`}>
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-bold text-red-600">Motif de la suppression</p>
                            <button onClick={() => setDelFor(null)} data-testid={`delete-cancel-${w.id}`} className="text-slate-400 hover:text-slate-600" aria-label="Annuler">
                              <X size={16} />
                            </button>
                          </div>
                          <textarea
                            rows={2}
                            value={delReason}
                            onChange={(e) => setDelReason(e.target.value)}
                            placeholder="Expliquez pourquoi vous supprimez ce travail (obligatoire)…"
                            data-testid={`delete-reason-${w.id}`}
                            className="field field-light mt-3 resize-none text-sm"
                          />
                          <button
                            onClick={() => deleteWork(w)}
                            disabled={delBusy || !delReason.trim()}
                            data-testid={`delete-confirm-${w.id}`}
                            className="mt-3 inline-flex items-center gap-2 rounded-lg bg-red-600 px-5 py-2.5 text-xs font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {delBusy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                            Confirmer la suppression
                          </button>
                        </div>
                      )}
                      {editId === w.id && editForm && (
                        <div className="mt-3 rounded-xl border border-korban-gold/40 bg-[#fffaf0] p-4" data-testid={`edit-panel-${w.id}`}>
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-bold text-korban-navy">Modifier ce travail</p>
                            <button onClick={() => setEditId(null)} data-testid={`edit-cancel-${w.id}`} className="text-slate-400 hover:text-slate-600" aria-label="Annuler">
                              <X size={16} />
                            </button>
                          </div>
                          <div className="mt-3 grid gap-3 sm:grid-cols-2">
                            <input
                              value={editForm.company}
                              onChange={(e) => setEditForm({ ...editForm, company: e.target.value })}
                              placeholder="Entreprise cliente"
                              data-testid={`edit-company-${w.id}`}
                              className="field field-light text-sm"
                            />
                            <input
                              value={editForm.title}
                              onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                              placeholder="Intitulé"
                              data-testid={`edit-title-${w.id}`}
                              className="field field-light text-sm"
                            />
                            <select
                              value={editForm.status}
                              onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                              data-testid={`edit-status-${w.id}`}
                              className="field field-light text-sm"
                            >
                              <option>À effectuer</option>
                              <option>En cours</option>
                              <option>Terminé</option>
                            </select>
                            <input
                              type="date"
                              value={editForm.work_date}
                              onChange={(e) => setEditForm({ ...editForm, work_date: e.target.value })}
                              data-testid={`edit-date-${w.id}`}
                              className="field field-light text-sm"
                            />
                          </div>
                          <textarea
                            rows={2}
                            value={editForm.description}
                            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                            placeholder="Description…"
                            data-testid={`edit-description-${w.id}`}
                            className="field field-light mt-3 resize-none text-sm"
                          />
                          <button
                            onClick={() => saveEdit(w)}
                            disabled={editBusy}
                            data-testid={`edit-save-${w.id}`}
                            className="mt-3 inline-flex items-center gap-2 rounded-lg bg-korban-navy px-5 py-2.5 text-xs font-bold text-korban-gold transition-all hover:-translate-y-0.5 hover:bg-korban-navy2 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {editBusy ? <Loader2 size={14} className="animate-spin" /> : <Pencil size={14} />}
                            Enregistrer les modifications
                          </button>
                        </div>
                      )}
                    </WorkCard>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
